// Referral attribution (spec docs/superpowers/specs/2026-06-12-referral-cashback-design.md):
// the invite code is captured as a VISIBLE register-form field, pre-filled from
// the URL ?ref= and threaded into the Google OAuth `state` — NO cookie, so
// there is no consent/ePrivacy question and no cross-host scoping to get wrong.
// The backend reads it from the register body (email) or the OAuth state (Google).

const CODE_RE = /^[a-z0-9]{4,12}$/i;

/** Pure: extract a valid ref code from a location.search string (for prefilling
 * the register field). Validates against CODE_RE and lowercases; null if absent
 * or malformed. */
export function parseRefParam(search: string): string | null {
  const code = new URLSearchParams(search).get('ref');
  return code && CODE_RE.test(code) ? code.toLowerCase() : null;
}

/** Pure: if a LANDING path carries a valid invite ?ref=, return the register URL
 * to forward to (so the code lands in the form field) — else null. The generated
 * invite URL is /app/register?ref= already; this only rescues a legacy/copied
 * /?ref= landing link, since the landing CTAs don't carry the query string and
 * there is no cookie to bridge it (codex / GitHub-codex #487 P1). No storage. */
export function landingRefRedirectTarget(pathname: string, search: string): string | null {
  const code = parseRefParam(search);
  if (!code) return null;
  // Preserve the explicit Chinese locale from a /zh landing link — detectLang
  // honors ?lang= (URL param > localStorage > navigator) and /app/register has no
  // /zh path mirror, so without this a zh invitee lands on the English register
  // page (GitHub-codex #487 P3).
  if (pathname === '/zh' || pathname === '/zh/') return `/app/register?ref=${code}&lang=zh`;
  if (pathname === '/') return `/app/register?ref=${code}`;
  return null;
}

/** Pure: validate a user-typed/prefilled referral code before sending it. Returns
 * the normalized (lowercased) code, or null when empty/malformed. Best-effort —
 * the backend re-validates and silently discards an unknown code. */
export function normalizeRefCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = raw.trim();
  return CODE_RE.test(v) ? v.toLowerCase() : null;
}
